/* eslint-disable max-lines */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@fuxi/eevee-icon';
import { useDispatch, useSelector } from 'react-redux';
import lookie from 'lookie';
import { useLocation } from 'react-router-dom';
import { AIConsultationFrom, TrackerCustomEvent, useTracker } from 'common-package';
import { useTimeout, useUpdateEffect } from 'ahooks';
import { ScreenOrientation, OrientationType } from '@capawesome/capacitor-screen-orientation';
import { useNav, useWeeklyPopup } from '@/hooks';
import servingAvatarImg from '@/assets/image/serving-avatar.png';
import defaultAvatarImg from '@/assets/image/default-avatar.png';
import defaultFemaleAvatarImg from '@/assets/image/default-women-avatar.png';
import service from '@/service';
import { DisclaimerDialog, Header, SlideModal, WriteOffDrawer } from '@/components';
import { selectDevicePlatform, selectedIsCanNavToMassageControl, setStoreInfo } from '@/store/device';
import {
  ALL_PAGES,
  CURRENT_ANDROID_ID,
  CURRENT_ORDER_IS_DRAFT,
  PartLists,
  SELECTED_CUSTOMER_ID,
  storagePrefixV2,
} from '@/constant';
import { Toast, getAgeFromBirthday, handleNavFromOrderInfo, showBirthday, transformVipName2Text } from '@/utils';
import useLogin from '@/hooks/useLogin';
import { selectCurrentServingOrder, setCurrentServingOrder } from '@/store/service';
import { DisclaimerDialogRefAttrs } from '@/components/disclaimer-dialog';
import ServiceRecordDrawer, { ServiceRecordDrawerRefAttrs } from '../service-record/components/ServiceRecordDrawer';

import cx from './index.module.scss';

export const memberVip = storagePrefixV2 + '/member-vip.png';

interface Props {}

const Home: React.FC<Props> = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const currentServingOrder = useSelector(selectCurrentServingOrder);
  const devicePlatform = useSelector(selectDevicePlatform);
  const isCanNavToMassageControl = useSelector(selectedIsCanNavToMassageControl);
  const dispatch = useDispatch();
  const { sendEvent } = useTracker();
  const { isLogin } = useLogin();
  const [isOnServicing, setIsOnServicing] = useState(false);
  const [loadingRemainTime, setLoadingRemainTime] = useState(undefined);
  const [wxOrderData, setWxOrderData] = useState<GetOrderFromWxResponse>();
  const [isGetServingOrder, setIsGetServingOrder] = useState(true);
  const [controlIsDrawerOpen, setControlIsDrawerOpen] = useState(false);
  const [customerInfo, setCustomerInfo] = useState<GetCustomerProfileResponse>(null);
  const isClickContinue = useRef(false);
  const serviceRecordDrawerRef = useRef<ServiceRecordDrawerRefAttrs>(null);
  const location = useLocation();
  const nav = useNav();
  const clauseDialogRef = useRef<DisclaimerDialogRefAttrs>(null);
  const [showModule, setShowModule] = useState<boolean>(true);
  const { shouldShowPopup } = useWeeklyPopup();
  const [isSlideModalVisible, setIsSlideModalVisible] = useState(false);
  const [r2PreheatInfo, setR2PreheatInfo] = useState<R2PreheatInfoResponse>(null);

  const isShowView3DModule = useMemo(() => {
    const { orderId, isNeedShowDrawerAfterFinish, customerId } = location?.state || {};
    return orderId && isNeedShowDrawerAfterFinish && customerId;
  }, [location?.state]);

  const isShowBanner = useMemo(() => {
    return r2PreheatInfo?.name && r2PreheatInfo?.imgUrl;
  }, [r2PreheatInfo?.imgUrl, r2PreheatInfo?.name]);

  useTimeout(() => {
    setLoadingRemainTime(undefined);
  }, loadingRemainTime);

  const getCreatedWxOrder = useCallback(async () => {
    const { data } = await service.service.getCreatedWxOrder();
    setWxOrderData(data);
  }, []);

  const getCustomerInfo = async customerId => {
    const { data } = await service.customer.getCustomerProfile(customerId);
    setCustomerInfo(data);
  };

  const getR2PreheatInfo = async () => {
    const { data } = await service.system.getR2PreheatInfo();
    setR2PreheatInfo(data);
  };

  const getBoundStoreInfo = async () => {
    const androidId = lookie.get(CURRENT_ANDROID_ID);
    try {
      const { data } = await service.system.getBoundStore(androidId);
      dispatch(setStoreInfo(data));
    } catch (e) {}
  };

  /** 获取当前按摩师正在进行的服务订单 */
  const getServingOrderByOperator = useCallback(async () => {
    try {
      const { data: servingOrder } = await service.service.getServingOrderByOperator();

      dispatch(setCurrentServingOrder(servingOrder));
      setIsGetServingOrder(false);
      if (servingOrder) {
        setIsOnServicing(!!servingOrder?.orderId);
        lookie.set(SELECTED_CUSTOMER_ID, servingOrder?.customerId);

        const { data } = await service.customer.getCustomerProfile(servingOrder?.customerId);
        setCustomerInfo(data);
      } else {
        lookie.remove(CURRENT_ORDER_IS_DRAFT);
        setIsOnServicing(false);
        dispatch(setCurrentServingOrder({} as any));
      }
    } catch {
      setIsGetServingOrder(false);
    }
  }, [dispatch]);

  const handleContinueTask = useCallback(async () => {
    if (isClickContinue.current) return;

    try {
      isClickContinue.current = true;

      const { data: newServingOrder } = await service.service.getServingOrderByOperator();
      dispatch(setCurrentServingOrder(newServingOrder));

      if (newServingOrder) {
        if (newServingOrder.step === 'RoboticTask' && !isCanNavToMassageControl) {
          return;
        }

        await handleNavFromOrderInfo(newServingOrder, nav);
      } else {
        setIsOnServicing(false);
        Toast.show('当前任务已结束');
      }
    } finally {
      isClickContinue.current = false;
    }
  }, [dispatch, isCanNavToMassageControl, nav]);

  useUpdateEffect(() => {
    // drawer关闭后查询当前是否有订单
    if (!controlIsDrawerOpen) {
      getCreatedWxOrder();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlIsDrawerOpen]);

  useEffect(() => {
    if (!isLogin) return;
    getServingOrderByOperator();
    getCreatedWxOrder();
    getR2PreheatInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLogin]);

  useEffect(() => {
    const { orderId, isNeedShowDrawerAfterFinish, customerId } = location?.state || {};
    if (orderId && isNeedShowDrawerAfterFinish) {
      service.service.getServiceOrder(orderId).then(data => {
        // 人工单不弹评价
        !data.data?.onlyManual && serviceRecordDrawerRef.current?.showHomePageDrawer(orderId);
      });
      if (customerId) {
        getCustomerInfo(customerId);
        const timer = setTimeout(() => {
          setShowModule(false);
        }, 2 * 60 * 1000); // 2分钟

        return () => {
          setCustomerInfo(null);
          clearTimeout(timer);
        };
      }
    }
  }, [location]);

  useEffect(() => {
    if (devicePlatform === 'android') {
      ScreenOrientation.lock({ type: OrientationType.LANDSCAPE_PRIMARY });
    }
  }, [devicePlatform]);

  return (
    <div className={cx('home')}>
      <Header showBack={false} showBorder={false} />
      <div className={cx('home-content', { 'is-serving-box': isOnServicing })}>
        {!isGetServingOrder && (
          <>
            {isOnServicing ? (
              <div className={cx('is-serving')} onClick={handleContinueTask}>
                <div className={cx('header')}>
                  <div
                    className={cx('header-left')}
                    style={{ backgroundImage: `url(${currentServingOrder?.userAvatar || servingAvatarImg})` }}
                  >
                    <img src={memberVip} alt="vip" className={cx('member-vip')} />
                  </div>

                  <div className={cx('header-right')}>
                    <div className={cx('header-right-box')}>
                      <div className={cx('user-info')}>
                        <div>{currentServingOrder.customerName}</div>
                        <div className={cx('line')}></div>
                        <div>{(customerInfo?.age ?? getAgeFromBirthday(customerInfo?.birthday)) + '岁'}</div>
                      </div>
                      <div className={cx('serving-text')}>服务中</div>
                    </div>
                    {currentServingOrder?.userVipName && (
                      <div className={cx('card-info', transformVipName2Text(currentServingOrder?.userVipName))}>
                        {currentServingOrder?.userVipName}
                      </div>
                    )}
                  </div>
                </div>
                <div className={cx('content')}>
                  <div className={cx('content-item')}>
                    <div className={cx('label')}>方案：</div>
                    <div className={cx('value')}>{currentServingOrder?.productName}</div>
                  </div>
                  <div className={cx('content-item', 'item-gap')}>
                    <div className={cx('label')}>部位：</div>
                    <div className={cx('body-part-value')}>
                      {PartLists?.find(item => item.partName === currentServingOrder?.bodyPart)?.name}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className={cx('operate-btns')}>
                {isShowView3DModule && showModule && (
                  <div
                    className={cx('physiotherapy-change')}
                    onClick={() => {
                      nav(ALL_PAGES.MEMBER_DETAIL, {
                        replace: true,
                      });
                    }}
                  >
                    <div className={cx('avatar-img')}>
                      <div className={cx('avatar')}>
                        <img
                          src={
                            customerInfo?.avatarUrl
                              ? customerInfo?.avatarUrl
                              : customerInfo?.gender === 'Female'
                              ? defaultFemaleAvatarImg
                              : defaultAvatarImg
                          }
                          alt="avatar"
                          className={cx('img')}
                          onError={e => ((e.target as HTMLImageElement).src = defaultAvatarImg)}
                        />
                        <img src={memberVip} alt="vip" className={cx('member-vip')} />
                      </div>

                      <div className={cx('text')}>
                        <div>去看看您的会员「{customerInfo?.name}」</div>
                        <div>的理疗变化吧</div>
                      </div>
                    </div>
                    <Icon name="arrow-right-enter" className={cx('icon')} />
                  </div>
                )}
                {isShowBanner && (
                  <div
                    className={cx('banner')}
                    onClick={() => {
                      sendEvent(TrackerCustomEvent.R2PreheatClick, {
                        et: 'CLK',
                        c1: r2PreheatInfo?.name,
                        c2: r2PreheatInfo?.imgUrl,
                      });
                      setIsSlideModalVisible(true);
                    }}
                  >
                    <span>{r2PreheatInfo?.name}</span>
                    <Icon name="arrow-fill-right" className={cx('icon')} />
                  </div>
                )}

                <div className={cx('top-area')}>
                  <div
                    className={cx('mini-program-box', 'operate-btn-box-bg')}
                    onClick={() => {
                      setIsDrawerOpen(true);
                      setControlIsDrawerOpen(true);
                    }}
                  >
                    {!!wxOrderData?.payOrderId ? (
                      <>
                        <div className={cx('user-info')}>
                          <div className={cx('avatar')}>
                            <img src={wxOrderData.avatarUrl} alt="avatar" />
                            <img src={memberVip} alt="vip" className={cx('member-vip')} />
                          </div>
                          <div className={cx('info-text')}>
                            <div className={cx('user-info-text')}>
                              <div className={cx('user-name')}>{wxOrderData?.userName}</div>
                              <div className={cx('line')}></div>
                              <div>{showBirthday(wxOrderData?.age, wxOrderData?.birthday)}</div>
                              <div className={cx('served-state')}>待服务</div>
                            </div>
                            <div className={cx('content')}>
                              <div className={cx('content-item')}>
                                <div className={cx('label')}>方案：</div>
                                <div className={cx('value')}>{wxOrderData?.goodsName}</div>
                              </div>
                              <div className={cx('content-item', 'item-gap')}>
                                <div className={cx('label')}>部位：</div>
                                <div className={cx('body-part-value')}>
                                  {PartLists?.find(item => item.partName === wxOrderData?.bodyPart)?.name}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className={cx('home-text')}>
                          <span>易启机器人小程序</span>
                          <span className={cx('line')}>|</span>
                          <span>核销码</span>
                        </div>
                        <Icon name="arrow-fill-right" className={cx('check-off-code')} />
                      </>
                    )}
                  </div>
                  <div
                    className={cx('ai-consultation', 'operate-btn-box-bg')}
                    onClick={() => {
                      sendEvent?.(TrackerCustomEvent.AiConsultationClk, {
                        et: 'CLK',
                        c1: AIConsultationFrom.Home,
                      });

                      nav(ALL_PAGES.SEARCH_MEMBER, {
                        state: {
                          action: 'ai-consultation',
                        },
                      });
                    }}
                  >
                    <div>AI问询</div>
                    <Icon className={cx('stars-icon')} name="stars" />
                  </div>
                </div>
                <div className={cx('pad-operate')}>
                  <div
                    className={cx('operate-btn-box-bg', 'home-text', 'pad-operate-item', 'start-service-box')}
                    onClick={async () => {
                      // 检查是否应该显示弹窗
                      if (shouldShowPopup()) {
                        clauseDialogRef.current?.show();
                      } else {
                        await getBoundStoreInfo();
                        nav(ALL_PAGES.SEARCH_MEMBER);
                      }
                    }}
                  >
                    <div>
                      <span>开始服务</span>
                      <Icon name="arrow-fill-right" className={cx('enter')} />
                    </div>
                  </div>

                  <div
                    className={cx('operate-btn-box-bg', 'home-text', 'pad-operate-item', 'add-member-box')}
                    onClick={async () => {
                      await getBoundStoreInfo();
                      nav(ALL_PAGES.ADD_MEMBER);
                    }}
                  >
                    <div>
                      <span>添加会员</span>
                      <Icon name="arrow-fill-right" className={cx('enter')} />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <div className={cx('compliance-tips')}>本设备不属于医疗器械，请勿用于医疗用途的治疗。</div>
      {controlIsDrawerOpen && (
        <WriteOffDrawer
          onVerifyWxOrder={getCreatedWxOrder}
          onOrderEndOfTimeout={() => setWxOrderData(null)}
          setLoadingRemainTime={setLoadingRemainTime}
          isDrawerOpen={isDrawerOpen}
          setIsDrawerOpen={setIsDrawerOpen}
          afterClose={() => setControlIsDrawerOpen(false)}
        />
      )}
      <ServiceRecordDrawer ref={serviceRecordDrawerRef} />
      <DisclaimerDialog ref={clauseDialogRef} />
      <SlideModal open={isSlideModalVisible} className={cx('banner-info')} contentClassName={cx('banner-info-content')}>
        <div className={cx('wrapper')}>
          <img src={r2PreheatInfo?.imgUrl} alt="" />
          <div className={cx('close-btn')} onClick={() => setIsSlideModalVisible(false)}>
            <Icon name="close-one" className={cx('close-icon')} />
          </div>
        </div>
      </SlideModal>
    </div>
  );
};

export default Home;
